import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { getCaseStudy } from "@/lib/caseStudies";
import { useLang } from "@/lib/i18n";
import { WhatsAppPill } from "./WhatsAppButton";
import { BIRDS } from "@/lib/brand";

export default function CaseStudyDialog({ brandName, open, onOpenChange }) {
  const { t } = useLang();
  const cs = brandName ? getCaseStudy(brandName) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`case-study-dialog-${brandName || ""}`}
        className="max-w-[1100px] w-[96vw] max-h-[92vh] p-0 overflow-hidden border border-black rounded-none bg-[#F4F4F0] text-black"
      >
        <DialogTitle className="sr-only">{brandName || "Case Study"}</DialogTitle>
        {!cs ? (
          <div className="p-10 font-mono text-[12px] tracking-[0.2em] uppercase">
            No case study available.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 overflow-y-auto max-h-[92vh]">
            {/* Left: hero image + stack */}
            <div className="md:col-span-7 relative bg-black">
              <div
                className="absolute inset-0 opacity-20 pointer-events-none z-10 mix-blend-multiply"
                style={{ background: `linear-gradient(180deg, transparent 40%, ${cs.accent} 140%)` }}
              />
              <motion.img
                initial={{ scale: 1.06, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                src={cs.images[0]}
                alt={brandName}
                className="w-full h-64 md:h-[420px] object-cover"
              />
              {cs.images.length >= 3 ? (
                <div className="grid grid-cols-2 gap-1 bg-black">
                  <img
                    src={cs.images[1]}
                    alt=""
                    className="w-full h-40 md:h-56 object-cover"
                  />
                  <img
                    src={cs.images[2]}
                    alt=""
                    className="w-full h-40 md:h-56 object-cover"
                  />
                </div>
              ) : cs.images.length === 2 ? (
                <img
                  src={cs.images[1]}
                  alt=""
                  className="w-full h-40 md:h-56 object-cover"
                />
              ) : (
                <div className="relative w-full h-32 md:h-40 bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
                  <div className="halftone absolute inset-0 text-[color:var(--ap-paper)]/[0.06]" />
                  <img
                    src={BIRDS.bird_hero}
                    alt=""
                    aria-hidden
                    className="w-24 h-24 object-contain opacity-90"
                  />
                  <div className="absolute right-4 bottom-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60">
                    In-house · Chikalthana MIDC
                  </div>
                </div>
              )}
            </div>

            {/* Right: content */}
            <div className="md:col-span-5 p-6 md:p-10 flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
                  {t("cs.printed_by")}
                </div>
                <span className="w-9 h-9 hidden" data-testid="case-study-close" />
              </div>

              <div className="mb-3">
                <span
                  className="inline-block px-2.5 py-1 border border-black font-mono text-[10px] tracking-[0.25em] uppercase"
                  style={{ background: cs.accent, color: "#0A0A0A" }}
                >
                  {t("cs.sector")} · {cs.sector}
                </span>
              </div>

              <h2 className="font-display italic text-4xl md:text-5xl leading-[1.02] tracking-tight">
                {brandName}
              </h2>
              <h3 className="font-display text-xl md:text-2xl mt-4 leading-snug">
                {cs.title}
              </h3>
              <p className="mt-6 text-[15px] leading-relaxed text-black/80">
                {cs.summary}
              </p>

              <div className="mt-8 border-t border-black pt-4">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-3">
                  {t("cs.services")}
                </div>
                <ul className="flex flex-wrap gap-2">
                  {cs.services.map((s) => (
                    <li
                      key={s}
                      className="border border-black px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase"
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-auto pt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#contact"
                  onClick={() => onOpenChange(false)}
                  data-testid="case-study-cta-quote"
                  className="btn-offset"
                >
                  Start a similar brief <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
                <WhatsAppPill
                  testId={`case-study-wa-${brandName}`}
                  label="Chat on WhatsApp"
                  message={`Hi Arvind Printers, I liked your ${brandName} case study — I'd like to explore a similar ${cs.sector.toLowerCase()} project.`}
                />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
